import { useMemo, useState, type FormEvent, type JSX } from 'react';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { ArrowLeft, ChevronDown, ChevronRight, Plus, Trash2 } from 'lucide-react';
import { Button, Card, CardContent, Input } from '@caratuva/ui';
import {
  CreateInvoiceInput,
  Incoterm,
  MIN_PAYABLE_USD,
  isBelowMinPayableUsd,
} from '@caratuva/shared-types';
import { ApiError, createInvoice, type CreateGetPaidInvoiceInput } from '../lib/api';
import {
  Field,
  FieldError,
  Section,
  SELF_SELLER_READINESS_CODES,
  SUPPORTED_CURRENCIES,
  SelfSellerReadinessBanner,
  selectClass,
} from '../components/invoice-form';

/*
 * GetPaid invoice-create form — SIMPLIFIED consumer flow.
 *
 * No seller picker: the org invoices as itself, so `createInvoice` never
 * sends `sellerExternalId` (see `CreateGetPaidInvoiceInput` in lib/api.ts).
 * Export trade details (incoterm, NCM/port/refs) are collapsed under an
 * optional disclosure — most GetPaid users never need them.
 *
 * Idempotency-Key is generated once with `crypto.randomUUID()` at mount and
 * stays stable across retries, so a double submit / network blip can't
 * create two invoices.
 */

export const Route = createFileRoute('/_app/invoices/new')({
  component: InvoiceCreate,
});

const INCOTERMS = Incoterm.options;

interface LineItemDraft {
  description: string;
  quantity: string;
  unitPrice: string;
}

function emptyLineItem(): LineItemDraft {
  return { description: '', quantity: '1', unitPrice: '0.00' };
}

function formatDecimal2(value: number): string {
  if (!Number.isFinite(value)) return '0.00';
  return value.toFixed(2);
}

function computeSubtotal(quantity: string, unitPrice: string): string {
  const q = Number.parseFloat(quantity);
  const p = Number.parseFloat(unitPrice);
  if (!Number.isFinite(q) || !Number.isFinite(p)) return '0.00';
  return formatDecimal2(q * p);
}

function newIdempotencyKey(): string {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }
  return `idem_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

type FieldErrors = Partial<Record<string, string>>;

function InvoiceCreate(): JSX.Element {
  const intl = useIntl();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [idempotencyKey] = useState<string>(() => newIdempotencyKey());

  const [buyerEmail, setBuyerEmail] = useState('');
  const [buyerName, setBuyerName] = useState('');
  const [buyerCountry, setBuyerCountry] = useState('');
  const [currency, setCurrency] = useState<string>('USD');
  const [dueDate, setDueDate] = useState('');
  const [lineItems, setLineItems] = useState<LineItemDraft[]>([emptyLineItem()]);

  // Export details are optional and collapsed by default — most GetPaid
  // (B2C) users never fill these in.
  const [exportDetailsOpen, setExportDetailsOpen] = useState(false);
  const [buyerRef, setBuyerRef] = useState('');
  const [exportContractRef, setExportContractRef] = useState('');
  const [incoterm, setIncoterm] = useState<string>('');
  const [portOfLoading, setPortOfLoading] = useState('');

  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [selfSellerNotReady, setSelfSellerNotReady] = useState(false);
  const [conflictInvoiceId, setConflictInvoiceId] = useState<string | null>(null);

  const subtotals = useMemo(
    () => lineItems.map((li) => computeSubtotal(li.quantity, li.unitPrice)),
    [lineItems],
  );

  const total = useMemo(() => {
    const sum = subtotals.reduce((acc, s) => acc + Number.parseFloat(s || '0'), 0);
    return formatDecimal2(sum);
  }, [subtotals]);

  const minPayableLabel = intl.formatNumber(MIN_PAYABLE_USD, {
    style: 'currency',
    currency: 'USD',
  });
  const totalNumber = Number.parseFloat(total);
  const belowMinimum = totalNumber > 0 && isBelowMinPayableUsd(total, currency);

  const mutation = useMutation({
    mutationFn: (input: CreateGetPaidInvoiceInput) => createInvoice(input, { idempotencyKey }),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
      void navigate({ to: '/invoices/$id', params: { id: data.id } });
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        const gate = (err.body as { error?: string } | null)?.error;
        if (gate && SELF_SELLER_READINESS_CODES.has(gate)) {
          setSelfSellerNotReady(true);
          return;
        }
        if (err.status === 400) {
          const next: FieldErrors = {};
          const body = err.body as { message?: unknown } | null;
          const messages = Array.isArray(body?.message)
            ? (body.message as string[])
            : typeof body?.message === 'string'
              ? [body.message]
              : [];
          for (const m of messages) {
            const match = /^([a-zA-Z0-9_.[\]]+)[:\s-]+\s*(.+)$/.exec(m);
            if (match && match[1] && match[2]) {
              next[match[1]] = match[2];
            }
          }
          setFieldErrors(next);
          setFormError(
            messages.length > 0
              ? messages.join('; ')
              : intl.formatMessage({
                  id: 'invoices.create.error.validation',
                  defaultMessage: 'Please fix the highlighted fields.',
                }),
          );
          return;
        }
        if (err.status === 409) {
          const body = err.body as { invoiceId?: string; id?: string } | null;
          const id = body?.invoiceId ?? body?.id ?? null;
          setConflictInvoiceId(id);
          setFormError(
            intl.formatMessage({
              id: 'invoices.create.error.conflict',
              defaultMessage: 'An invoice with this reference already exists.',
            }),
          );
          return;
        }
      }
      setFormError(
        err instanceof Error
          ? err.message
          : intl.formatMessage({
              id: 'invoices.create.error.generic',
              defaultMessage: 'Failed to create invoice.',
            }),
      );
    },
  });

  function addLineItem(): void {
    setLineItems((items) => [...items, emptyLineItem()]);
  }

  function removeLineItem(index: number): void {
    setLineItems((items) => (items.length <= 1 ? items : items.filter((_, i) => i !== index)));
  }

  function updateLineItem(index: number, patch: Partial<LineItemDraft>): void {
    setLineItems((items) => items.map((it, i) => (i === index ? { ...it, ...patch } : it)));
  }

  function onSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setFieldErrors({});
    setFormError(null);
    setSelfSellerNotReady(false);
    setConflictInvoiceId(null);

    const candidate: Record<string, unknown> = {
      currency: currency.trim().toUpperCase(),
      amount: total,
      buyerEmail: buyerEmail.trim(),
      buyerName: buyerName.trim(),
      lineItems: lineItems.map((li, i) => ({
        description: li.description.trim(),
        quantity: li.quantity.trim(),
        unitPrice: li.unitPrice.trim(),
        subtotal: subtotals[i] ?? '0.00',
      })),
    };
    if (buyerCountry.trim()) candidate.buyerCountry = buyerCountry.trim().toUpperCase();
    if (dueDate) candidate.dueDate = new Date(dueDate).toISOString();
    if (buyerRef.trim()) candidate.buyerRef = buyerRef.trim();
    if (exportContractRef.trim()) candidate.exportContractRef = exportContractRef.trim();
    if (incoterm) candidate.incoterm = incoterm;
    if (portOfLoading.trim()) candidate.portOfLoading = portOfLoading.trim();

    // GetPaid never names a seller — CreateInvoiceInput makes sellerExternalId
    // optional (self-seller flow), so we validate against the shared-types
    // schema directly and simply never set the field.
    const parsed = CreateInvoiceInput.safeParse(candidate);
    if (!parsed.success) {
      const next: FieldErrors = {};
      for (const issue of parsed.error.issues) {
        const path = issue.path.join('.');
        if (!next[path]) {
          next[path] = issue.message;
        }
      }
      setFieldErrors(next);
      setFormError(
        intl.formatMessage({
          id: 'invoices.create.error.validation',
          defaultMessage: 'Please fix the highlighted fields.',
        }),
      );
      return;
    }

    mutation.mutate(parsed.data);
  }

  function lineItemError(
    index: number,
    field: keyof LineItemDraft | 'subtotal',
  ): string | undefined {
    return fieldErrors[`lineItems.${index}.${field}`];
  }

  return (
    <section className="mx-auto max-w-5xl">
      <Link
        to="/invoices"
        className="inline-flex items-center gap-1 text-sm text-navy-700 transition-colors hover:text-navy-900"
      >
        <ArrowLeft aria-hidden className="h-4 w-4" strokeWidth={1.5} />
        {intl.formatMessage({ id: 'common.nav.invoices', defaultMessage: 'Invoices' })}
      </Link>

      <div className="mt-3 mb-10 border-b border-navy-100 pb-6">
        <h1 className="text-2xl font-semibold tracking-tight text-navy-900">
          {intl.formatMessage({ id: 'invoices.create.title', defaultMessage: 'New invoice' })}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {intl.formatMessage({
            id: 'invoices.create.subtitle',
            defaultMessage: 'Send an invoice to your buyer and start tracking payment.',
          })}
        </p>
      </div>

      {selfSellerNotReady ? (
        <div className="mb-8">
          <SelfSellerReadinessBanner />
          <Link
            to="/onboarding"
            className="mt-2 inline-block text-sm font-medium text-amber-800 underline"
          >
            {intl.formatMessage({
              id: 'invoices.create.error.selfSellerNotReady.cta',
              defaultMessage: 'Finish onboarding',
            })}
          </Link>
        </div>
      ) : null}

      <form onSubmit={onSubmit} className="space-y-10" noValidate>
        <Section
          title={intl.formatMessage({
            id: 'invoices.create.section.buyer',
            defaultMessage: 'Buyer',
          })}
          description={intl.formatMessage({
            id: 'invoices.create.section.buyer.description',
            defaultMessage: 'Who this invoice is sent to.',
          })}
        >
          <Card className="border-navy-100 shadow-xs">
            <CardContent className="space-y-5 p-6">
              <div className="grid gap-5 sm:grid-cols-2">
                <Field
                  id="buyerEmail"
                  label={intl.formatMessage({
                    id: 'invoices.create.field.buyerEmail',
                    defaultMessage: 'Buyer email',
                  })}
                  required
                  error={fieldErrors.buyerEmail}
                >
                  <Input
                    id="buyerEmail"
                    type="email"
                    autoComplete="email"
                    required
                    value={buyerEmail}
                    onChange={(e) => setBuyerEmail(e.target.value)}
                  />
                </Field>
                <Field
                  id="buyerName"
                  label={intl.formatMessage({
                    id: 'invoices.create.field.buyerName',
                    defaultMessage: 'Buyer name',
                  })}
                  required
                  error={fieldErrors.buyerName}
                >
                  <Input
                    id="buyerName"
                    required
                    value={buyerName}
                    onChange={(e) => setBuyerName(e.target.value)}
                  />
                </Field>
              </div>
              <div className="grid gap-5 sm:grid-cols-2">
                <Field
                  id="buyerCountry"
                  label={intl.formatMessage({
                    id: 'invoices.create.field.buyerCountry',
                    defaultMessage: 'Buyer country (optional)',
                  })}
                  error={fieldErrors.buyerCountry}
                >
                  <Input
                    id="buyerCountry"
                    maxLength={2}
                    placeholder="US"
                    value={buyerCountry}
                    onChange={(e) => setBuyerCountry(e.target.value.toUpperCase())}
                  />
                </Field>
              </div>
            </CardContent>
          </Card>
        </Section>

        <Section
          title={intl.formatMessage({
            id: 'invoices.create.section.lineItems',
            defaultMessage: 'Line items',
          })}
          description={intl.formatMessage({
            id: 'invoices.create.section.lineItems.description',
            defaultMessage: 'Subtotal is computed as quantity multiplied by unit price.',
          })}
        >
          <Card className="overflow-hidden border-navy-100 shadow-xs">
            <CardContent className="p-0">
              <div className="hidden grid-cols-12 gap-3 border-b border-navy-100 px-6 py-3 text-[11px] font-medium uppercase tracking-wider text-navy-700 sm:grid">
                <div className="col-span-5">
                  {intl.formatMessage({
                    id: 'invoices.create.field.description',
                    defaultMessage: 'Description',
                  })}
                </div>
                <div className="col-span-2">
                  {intl.formatMessage({
                    id: 'invoices.create.field.quantity',
                    defaultMessage: 'Quantity',
                  })}
                </div>
                <div className="col-span-2">
                  {intl.formatMessage({
                    id: 'invoices.create.field.unitPrice',
                    defaultMessage: 'Unit price',
                  })}
                </div>
                <div className="col-span-2 text-right">
                  {intl.formatMessage({
                    id: 'invoices.create.field.subtotal',
                    defaultMessage: 'Subtotal',
                  })}
                </div>
                <div className="col-span-1" />
              </div>

              <div className="divide-y divide-navy-100">
                {lineItems.map((item, index) => (
                  <div
                    key={index}
                    className="grid grid-cols-1 items-start gap-3 px-6 py-4 transition-colors hover:bg-beige-50/40 sm:grid-cols-12"
                  >
                    <div className="sm:col-span-5">
                      <Input
                        aria-label={intl.formatMessage(
                          {
                            id: 'invoices.create.field.descriptionAria',
                            defaultMessage: 'Description for line {index}',
                          },
                          { index: index + 1 },
                        )}
                        placeholder={intl.formatMessage({
                          id: 'invoices.create.field.description.placeholder',
                          defaultMessage: 'Item or service',
                        })}
                        value={item.description}
                        onChange={(e) => updateLineItem(index, { description: e.target.value })}
                      />
                      {lineItemError(index, 'description') ? (
                        <FieldError>{lineItemError(index, 'description')}</FieldError>
                      ) : null}
                    </div>
                    <div className="sm:col-span-2">
                      <Input
                        aria-label={intl.formatMessage(
                          {
                            id: 'invoices.create.field.quantityAria',
                            defaultMessage: 'Quantity for line {index}',
                          },
                          { index: index + 1 },
                        )}
                        inputMode="decimal"
                        className="text-right tabular-nums"
                        value={item.quantity}
                        onChange={(e) => updateLineItem(index, { quantity: e.target.value })}
                      />
                      {lineItemError(index, 'quantity') ? (
                        <FieldError>{lineItemError(index, 'quantity')}</FieldError>
                      ) : null}
                    </div>
                    <div className="sm:col-span-2">
                      <Input
                        aria-label={intl.formatMessage(
                          {
                            id: 'invoices.create.field.unitPriceAria',
                            defaultMessage: 'Unit price for line {index}',
                          },
                          { index: index + 1 },
                        )}
                        inputMode="decimal"
                        className="text-right tabular-nums"
                        value={item.unitPrice}
                        onChange={(e) => updateLineItem(index, { unitPrice: e.target.value })}
                      />
                      {lineItemError(index, 'unitPrice') ? (
                        <FieldError>{lineItemError(index, 'unitPrice')}</FieldError>
                      ) : null}
                    </div>
                    <div className="self-center text-right text-sm font-medium tabular-nums text-navy-900 sm:col-span-2">
                      {subtotals[index] ?? '0.00'}
                    </div>
                    <div className="flex justify-end self-center sm:col-span-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeLineItem(index)}
                        disabled={lineItems.length <= 1}
                        aria-label={intl.formatMessage(
                          {
                            id: 'invoices.create.removeLineItemAria',
                            defaultMessage: 'Remove line {index}',
                          },
                          { index: index + 1 },
                        )}
                        className="text-navy-600 hover:bg-danger/10 hover:text-danger"
                      >
                        <Trash2 aria-hidden className="h-4 w-4" strokeWidth={1.5} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              {fieldErrors.lineItems ? (
                <div className="px-6 pt-3">
                  <FieldError>{fieldErrors.lineItems}</FieldError>
                </div>
              ) : null}

              <div className="flex items-center justify-between border-t border-navy-100 bg-beige-50/60 px-6 py-4">
                <Button type="button" variant="outline" size="sm" onClick={addLineItem}>
                  <Plus aria-hidden className="h-4 w-4" strokeWidth={1.5} />
                  {intl.formatMessage({
                    id: 'invoices.create.addLineItem',
                    defaultMessage: 'Add line item',
                  })}
                </Button>
                <div className="flex items-baseline gap-3">
                  <span className="text-xs font-medium uppercase tracking-wider text-navy-700">
                    {intl.formatMessage({ id: 'invoices.create.total', defaultMessage: 'Total' })}
                  </span>
                  <span className="font-mono text-lg font-semibold tabular-nums text-navy-900">
                    {total}
                  </span>
                  <span className="text-sm font-medium text-navy-700">{currency}</span>
                </div>
              </div>

              <div className="border-t border-navy-100 px-6 py-3">
                {belowMinimum ? (
                  <p className="text-xs text-[var(--color-danger)]" role="alert">
                    {intl.formatMessage(
                      {
                        id: 'invoices.create.minAmount.below',
                        defaultMessage:
                          'Below the {min} minimum your buyer can pay by bank transfer — increase the total or this invoice can’t be funded online.',
                      },
                      { min: minPayableLabel },
                    )}
                  </p>
                ) : (
                  <p className="text-xs text-navy-600">
                    {intl.formatMessage(
                      {
                        id: 'invoices.create.minAmount.note',
                        defaultMessage:
                          'Buyers pay by bank transfer through our payments partner, which requires a minimum of about {min} (or equivalent). Smaller invoices can’t be funded online.',
                      },
                      { min: minPayableLabel },
                    )}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </Section>

        <Section
          title={intl.formatMessage({
            id: 'invoices.create.section.details',
            defaultMessage: 'Invoice details',
          })}
          description={intl.formatMessage({
            id: 'invoices.create.section.details.description',
            defaultMessage: 'Currency and an optional due date.',
          })}
        >
          <Card className="border-navy-100 shadow-xs">
            <CardContent className="grid gap-5 p-6 sm:grid-cols-2">
              <Field
                id="currency"
                label={intl.formatMessage({
                  id: 'invoices.create.field.currency',
                  defaultMessage: 'Currency',
                })}
                error={fieldErrors.currency}
              >
                <select
                  id="currency"
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  className={selectClass}
                >
                  {SUPPORTED_CURRENCIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </Field>
              <Field
                id="dueDate"
                label={intl.formatMessage({
                  id: 'invoices.create.field.dueDate',
                  defaultMessage: 'Due date (optional)',
                })}
                error={fieldErrors.dueDate}
              >
                <Input
                  id="dueDate"
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                />
              </Field>
            </CardContent>
          </Card>
        </Section>

        <section>
          <button
            type="button"
            onClick={() => setExportDetailsOpen((v) => !v)}
            aria-expanded={exportDetailsOpen}
            className="flex items-center gap-1.5 text-sm font-medium text-navy-700 transition-colors hover:text-navy-900"
          >
            {exportDetailsOpen ? (
              <ChevronDown aria-hidden className="h-4 w-4" strokeWidth={1.5} />
            ) : (
              <ChevronRight aria-hidden className="h-4 w-4" strokeWidth={1.5} />
            )}
            {intl.formatMessage({
              id: 'invoices.create.exportDetails.toggle',
              defaultMessage: 'Export details (optional)',
            })}
          </button>
          {exportDetailsOpen ? (
            <Card className="mt-3 border-navy-100 shadow-xs">
              <CardContent className="grid gap-5 p-6 sm:grid-cols-2">
                <Field
                  id="buyerRef"
                  label={intl.formatMessage({
                    id: 'invoices.create.field.buyerRef',
                    defaultMessage: 'Buyer reference',
                  })}
                  error={fieldErrors.buyerRef}
                >
                  <Input
                    id="buyerRef"
                    value={buyerRef}
                    onChange={(e) => setBuyerRef(e.target.value)}
                  />
                </Field>
                <Field
                  id="exportContractRef"
                  label={intl.formatMessage({
                    id: 'invoices.create.field.exportContractRef',
                    defaultMessage: 'Export contract ref',
                  })}
                  error={fieldErrors.exportContractRef}
                >
                  <Input
                    id="exportContractRef"
                    value={exportContractRef}
                    onChange={(e) => setExportContractRef(e.target.value)}
                  />
                </Field>
                <Field
                  id="incoterm"
                  label={intl.formatMessage({
                    id: 'invoices.create.field.incoterm',
                    defaultMessage: 'Incoterm',
                  })}
                  error={fieldErrors.incoterm}
                >
                  <select
                    id="incoterm"
                    value={incoterm}
                    onChange={(e) => setIncoterm(e.target.value)}
                    className={selectClass}
                  >
                    <option value="">
                      {intl.formatMessage({
                        id: 'invoices.create.field.incoterm.none',
                        defaultMessage: 'None',
                      })}
                    </option>
                    {INCOTERMS.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field
                  id="portOfLoading"
                  label={intl.formatMessage({
                    id: 'invoices.create.field.portOfLoading',
                    defaultMessage: 'Port of loading',
                  })}
                  error={fieldErrors.portOfLoading}
                >
                  <Input
                    id="portOfLoading"
                    value={portOfLoading}
                    onChange={(e) => setPortOfLoading(e.target.value)}
                  />
                </Field>
              </CardContent>
            </Card>
          ) : null}
        </section>

        {formError ? (
          <div
            role="alert"
            className="rounded-md border border-[var(--color-danger)] bg-[var(--color-danger)]/10 p-3 text-sm text-[var(--color-danger)]"
          >
            <p>{formError}</p>
            {conflictInvoiceId ? (
              <p className="mt-2">
                <Link to="/invoices/$id" params={{ id: conflictInvoiceId }} className="underline">
                  {intl.formatMessage({
                    id: 'invoices.create.error.conflict.link',
                    defaultMessage: 'Open existing invoice',
                  })}
                </Link>
              </p>
            ) : null}
          </div>
        ) : null}

        <div className="flex items-center justify-end gap-3 border-t border-navy-100 pt-6">
          <Button asChild variant="ghost">
            <Link to="/invoices">
              {intl.formatMessage({ id: 'invoices.create.cancel', defaultMessage: 'Cancel' })}
            </Link>
          </Button>
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending
              ? intl.formatMessage({
                  id: 'invoices.create.submitting',
                  defaultMessage: 'Creating...',
                })
              : intl.formatMessage({
                  id: 'invoices.create.submit',
                  defaultMessage: 'Create invoice',
                })}
          </Button>
        </div>
      </form>
    </section>
  );
}
